import { actions, elements, inputs } from '@ecosystem/newron-design-system';
import React, { forwardRef, Ref, useImperativeHandle, useState } from 'react';
import { MAX_ALLOWED_CUSTOM_CLASSES } from '../../../common/selection.menu/constants';
import { MenuSeparatorComponent } from '../../../common/selection.menu/menu.separator';
import { FilterDefinition } from '../../../types';
import { CustomCategoricalClass } from '../../../types/content/custom.types';
import constants from '../../constants';
import { CustomClassLabel } from '../customClassLabel';
import { AddClassButton } from '../addClassButton';
import { updateLabelErrors } from '../../utils';

const { Icon } = elements;
const { IconButton } = actions;
const { MultiSelect } = inputs;
const { LABEL_REQUIRED, OPTION_REQUIRED } = constants;

interface CategoricalFeatureRef {
  setTotalAvailableCategories: (availableCategories: number) => void;
  setEmptySelectedOption: (emptySelectedOptionClass: number[]) => void;
  setEmptyLabel: (emptyLabelClass: number[]) => void;
}

interface CategoricalFeatureProps {
  featureClasses: CustomCategoricalClass[];
  selectedFeature: FilterDefinition;
  setFeatureClasses: React.Dispatch<React.SetStateAction<CustomCategoricalClass[]>>;
  forwardedRef?: Ref<CategoricalFeatureRef>;
}

export const CategoricalFeature = forwardRef<CategoricalFeatureRef, CategoricalFeatureProps>((props, ref) => {
  return <CategoricalFeatureComponent {...props} forwardedRef={ref} />;
});
const CategoricalFeatureComponent: React.FC<CategoricalFeatureProps> = ({
  featureClasses,
  selectedFeature,
  setFeatureClasses,
  forwardedRef,
}) => {
  const [totalAvailableCategories, setTotalAvailableCategories] = useState<number>(selectedFeature.options.length);
  const [emptySelectedOption, setEmptySelectedOption] = useState<number[]>([]);
  const [emptyLabel, setEmptyLabel] = useState<number[]>([]);

  //variables for info message
  const infoMessage1 = `${totalAvailableCategories} ${totalAvailableCategories > 1 ? 'values ' : 'value '}`;
  const infoMessage2 = `custom class ${totalAvailableCategories > 1 ? 'are' : 'is'} inherited`;

  useImperativeHandle(forwardedRef, () => ({
    setTotalAvailableCategories: (availableCategories: number) => setTotalAvailableCategories(availableCategories),
    setEmptySelectedOption: (emptySelectedOptionClass: number[]) => setEmptySelectedOption(emptySelectedOptionClass),
    setEmptyLabel: (emptyLabelClass: number[]) => setEmptyLabel(emptyLabelClass),
  }));

  const handleOnLabelChange = (index: number) => (label: string) => {
    const updatedFeatureClass = { ...featureClasses[index] };
    const copyFeatureClass = [...featureClasses];
    updatedFeatureClass.label = label;
    copyFeatureClass[index] = updatedFeatureClass;
    updatedFeatureClasses(copyFeatureClass);
    if (emptyLabel.includes(index) && label) {
      setEmptyLabel(emptyLabel.filter(e => e !== index));
    }
  };

  const updatedFeatureClasses = (featureClasses: CustomCategoricalClass[]) => {
    setFeatureClasses(updateLabelErrors(featureClasses));
  };

  const handleOnFeatureChange = (index: number) => (values: string[], value: string) => {
    const copyFeatureClasses = [...featureClasses];
    copyFeatureClasses[index].selectedOptions = values;

    const isSelected = values.includes(value);

    if (isSelected) {
      //handle selecting category-- remove a selected option from the option list of other class
      removeOptionFromOtherClasses(copyFeatureClasses, index, value);
      removeIndexFromEmptySelected(index);
    } else {
      addOptionBackToOtherClasses(copyFeatureClasses, index, value);
    }
    setTotalAvailableCategories(getAvailableCategories().length);
    updatedFeatureClasses(copyFeatureClasses);
  };

  const removeOptionFromOtherClasses = (classes: typeof featureClasses, currentIndex: number, value: string): void => {
    classes.forEach((fc, fcIndex) => {
      if (fcIndex !== currentIndex) {
        fc.options = fc.options.filter(option => option.value !== value);
      }
    });
  };

  const removeIndexFromEmptySelected = (index: number): void => {
    if (emptySelectedOption.includes(index)) {
      setEmptySelectedOption(emptySelectedOption.filter(i => i !== index));
    }
  };

  const addOptionBackToOtherClasses = (classes: typeof featureClasses, currentIndex: number, value: string): void => {
    //handle unselecting category-- add the unselected option to the option list of other class
    const toAddOption = selectedFeature.options.find(option => option.value === value);
    if (!toAddOption) return;

    classes.forEach((fc, i) => {
      if (i !== currentIndex) {
        const newOptions = [...fc.options, toAddOption];
        // to ensure the order of the options always matches its initial order
        fc.options = selectedFeature.options.filter(option => newOptions.includes(option));
      }
    });
  };

  const addFeatureClass = () => {
    const newFeatureTier: CustomCategoricalClass = {
      label: '',
      selectedOptions: [],
      options: getAvailableCategories(),
    };
    updatedFeatureClasses([...featureClasses, newFeatureTier]);
  };

  const deleteFeatureClass = (index: number) => {
    const featureClassToUpdate = adjustClassesAfterDeletion(index, featureClasses);
    const filteredList = featureClassToUpdate.filter((_, i) => i !== index);
    const selectedCategories = filteredList.flatMap(fc => fc.selectedOptions);
    const unselected = selectedFeature.options.filter(option => !selectedCategories.includes(option.value));

    updatedFeatureClasses(filteredList);
    setTotalAvailableCategories(unselected.length);
  };

  const adjustClassesAfterDeletion = (index: number, classes: CustomCategoricalClass[]): CustomCategoricalClass[] => {
    const copyFeatureClasses = [...classes];
    const toRemove = classes[index];
    if (toRemove.selectedOptions.length) {
      //add back the selected options from deleted class to all classes option list
      const toAddOptions = selectedFeature.options.filter(option => toRemove.selectedOptions.includes(option.value));
      copyFeatureClasses.forEach((fc, fcIndex) => {
        if (fcIndex === index) return;
        const newOptions = [...fc.options, ...toAddOptions];
        const sortedOptions = selectedFeature.options.filter(option => newOptions.includes(option));
        fc.options = sortedOptions;
      });
    }
    return copyFeatureClasses;
  };

  const getAvailableCategories = () => {
    //the options of next classes should be limited to unselected categories
    const selectedCategories = featureClasses.flatMap(fc => fc.selectedOptions);
    return selectedFeature.options.filter(option => !selectedCategories.includes(option.value));
  };

  const renderFeatureClasses = (index: number, cp: CustomCategoricalClass) => {
    return (
      <div className="pt-5">
        <div className="relative pb-2" data-test-id={`select-feature-${index}`}>
          <MultiSelect
            name={`featureClasses-${index}`}
            onChange={handleOnFeatureChange(index)}
            options={featureClasses[index].options}
            values={featureClasses[index].selectedOptions}
            placeholder="Assign values ..."
            errorMessage={emptySelectedOption.includes(index) ? OPTION_REQUIRED : ''}
          />
        </div>
        <CustomClassLabel
          customClasses={featureClasses}
          labelOnChangeFn={handleOnLabelChange(index)}
          index={index}
          isLabelOptional={false}
          errorMessage={emptyLabel.includes(index) ? LABEL_REQUIRED : cp.labelError}
        />
      </div>
    );
  };

  return (
    <div>
      {!!featureClasses.length && (
        <div className="pt-8 pb-6 flex-grow-0 modal-body overflow-visible">
          <div className="flex-row gap-2">
            <div className="flex gap-1 self-center">
              <span className="text-heading text-md font-bold">Classes</span>
              <span className="text-inactive text-md">{`(${featureClasses.length})`}</span>
            </div>
          </div>

          <MenuSeparatorComponent key={`seperator_main`} />
          {featureClasses.map((classItem, index, cp) => {
            return (
              <React.Fragment key={`fr_${index + cp.length}`}>
                {renderFeatureClasses(index, classItem)}
                {cp.length > 1 ? (
                  <IconButton
                    key={`delete_${index}`}
                    testId={`delete-feature-class-btn-${index}`}
                    onClick={() => deleteFeatureClass(index)}
                  >
                    <Icon.icons.Remove />
                  </IconButton>
                ) : null}
                <MenuSeparatorComponent key={`seperator_${index}`} />
              </React.Fragment>
            );
          })}

          {totalAvailableCategories > 0 && (
            <div className="flex justify-between">
              <AddClassButton
                testId={'add-feature-class-btn'}
                onClick={addFeatureClass}
                isShowing={
                  featureClasses.length < MAX_ALLOWED_CUSTOM_CLASSES &&
                  featureClasses.length < selectedFeature.options.length
                }
              />

              <div className="flex flex-column text-inactive" data-test-id="feature-class-info-message">
                <div className="self-end">
                  <span className="font-bold text-sm">{infoMessage1}</span>
                  <span className="text-xs">not assigned to a</span>
                </div>
                <div className="text-xs self-end">{infoMessage2}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
